<template>
  <Modal
    :modal-style="modalStyle"
    title="History of changes"
    @close="$emit('close')"
  >
    <QTable
      :columns="columns"
      flat
      hide-bottom
      row-key="id"
      :rows="versions"
    >
      <template #header>
        <QTr>
          <QTh class="text-left"> Expand </QTh>
          <QTh class="text-left"> Updated at </QTh>
          <QTh class="text-left"> Updated by </QTh>
          <QTh class="text-left"> Event </QTh>
          <QTh class="text-left"> Changed fields </QTh>
          <QTh
            v-if="hasFeature"
            class="text-left"
          >
            Feature
          </QTh>
          <QTh
            v-if="extraLabel"
            class="text-left"
          >
            {{ extraLabel }}
          </QTh>
        </QTr>
      </template>

      <template #body="props">
        <QTr :props="props">
          <QTd
            key="expand"
            :props="props"
          >
            <QBtn
              color="primary"
              dense
              :icon="props.expand ? 'remove' : 'add'"
              @click="props.expand = !props.expand"
            />
          </QTd>
          <QTd
            key="updated_at"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            {{
              formatIntlDate(props.row.createdAt, {
                dateStyle: "short",
                timeStyle: "medium",
              })
            }}
            <div class="soft">({{ updatedAt(props.row) }})</div>
          </QTd>
          <QTd
            key="whodunnit"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            {{ userName(props.row) }}
          </QTd>
          <QTd
            key="event"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            {{ props.row.event }}
          </QTd>
          <QTd
            key="object_changes"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            <div v-if="props.row.objectChanges">
              <div
                v-if="
                  isFeatureAllowanceVersion(props.row) &&
                  isCreateEvent(props.row)
                "
              >
                {{ contractForAllowance(props.row) }}
              </div>

              <div
                v-for="field in objectChanges(props.row.objectChanges)"
                v-else
                :key="field"
              >
                {{ field }}
              </div>
            </div>
          </QTd>
          <QTd
            v-if="extraLabel"
            key="contract"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            {{ props.row.itemId }}
          </QTd>
          <QTd
            v-if="hasFeature"
            key="feature"
            class="wrap-table-content vertical-top"
            :props="props"
          >
            {{ featureToDisplay(props.row) }}
          </QTd>
        </QTr>
        <QTr
          v-show="props.expand"
          :props="props"
        >
          <QTd colspan="100%">
            <div v-if="props.row.objectChanges">
              <VersionControlItemChangesTable
                :changes="props.row.objectChanges"
              />
            </div>
          </QTd>
        </QTr>
      </template>
    </QTable>
  </Modal>
</template>

<script>
import Modal from "shared/components/core/modals/Modal.vue";
import { organisationContractTypes } from "shared/constants";
import { formatIntlDate, fromNow } from "shared/helpers/date";
import { Version } from "shared/resources";

import VersionControlItemChangesTable from "src/components/VersionControlItemChangesTable.vue";

export default {
  name: "VersionControlModal",
  components: {
    Modal,
    VersionControlItemChangesTable,
  },
  props: {
    modalWidth: {
      type: String,
      default: "",
    },
    versions: {
      type: Array,
      default: () => [],
    },
    hasFeature: {
      type: Boolean,
    },
    extraLabel: {
      type: String,
      default: "",
    },
    item: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ["close"],
  data() {
    return {
      columns: [
        {
          name: "expand",
          required: false,
          label: "expand",
          field: "expand",
          align: "left",
          style: "width: 10%",
          classes: "wrap-table-content",
        },
        {
          name: "updated_at",
          required: false,
          label: "Updated at",
          field: "updated_at",
          align: "left",
          style: "width: 30%",
          classes: "wrap-table-content",
        },
        {
          name: "whodunnit",
          required: false,
          label: "Updated by",
          field: "whodunnit",
          align: "left",
          style: "width: 20%",
          classes: "wrap-table-content",
        },
        {
          name: "event",
          required: false,
          label: "Event",
          field: "event",
          align: "left",
          style: "width: 10%",
          classes: "wrap-table-content",
        },
        {
          name: "object_changes",
          required: false,
          label: "Changed fields",
          field: "objectChanges",
          align: "left",
          style: "width: 40%",
          classes: "wrap-table-content",
        },
        {
          name: "feature",
          required: false,
          label: "Feature",
          field: "feature",
          align: "left",
          style: "width: 20%",
          classes: "wrap-table-content",
        },
        {
          name: "contract",
          required: false,
          label: "Contract",
          field: "itemId",
          align: "left",
          style: "width: 20%",
          classes: "wrap-table-content",
        },
      ],
    };
  },
  computed: {
    modalStyle() {
      return this.modalWidth
        ? { maxWidth: this.modalWidth }
        : { width: "800px" };
    },
  },
  mounted() {
    if (this.item.id && this.versions.length === 0) {
      this.loadVersions();
    }
  },
  methods: {
    formatIntlDate,
    async loadVersions() {
      try {
        if (this.item.id && this.item.type) {
          this.versions = (
            await Version.where({
              itemId: this.item.id,
              itemType: { eql: this.item.type },
            })
              .includes(["user"])
              .order({ id: "desc" })
              .per(5)
              .all()
          ).data;
        }
      } catch (error) {
        this.$error("Error loading versions", error);
      }
    },
    isCreateEvent(version) {
      return version.event === "create";
    },
    isFeatureAllowanceVersion(version) {
      return version.itemType === "OrganisationFeatureAllowance";
    },
    contractForAllowance(version) {
      const contract = version.item.organisationContract;

      const contractType = this.contractType(contract.contractType).label;

      return `${contractType} (Expires: ${contract.contractExpiry})`;
    },
    contractType(key) {
      return organisationContractTypes.find(
        (contractType) => contractType.id === key
      );
    },
    updatedAt(version) {
      return fromNow(version.createdAt);
    },
    featureToDisplay(version) {
      if (
        this.isFeatureAllowanceVersion(version) ||
        this.isCreateEvent(version)
      ) {
        return version.item.organisationsFeature.name;
      }

      return version.object.feature;
    },
    objectChanges(changes) {
      return Object.keys(changes).filter((key) => key !== "updated_at");
    },
    userName(version) {
      if (version.user && version.user.name) {
        return version.user.name;
      }

      return "nil";
    },
  },
};
</script>
